import { Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTenantUsers, useUpdateUserRole } from '@/hooks/use-admin';
import type { TenantUser } from '@/hooks/use-admin';

const VALID_ROLES = ['owner', 'admin', 'sales', 'member'] as const;

const roleBadgeClass: Record<string, string> = {
  owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  admin: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  sales: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  member: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
};

function RoleDropdown({ user }: { user: TenantUser }) {
  const updateRole = useUpdateUserRole();

  function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    updateRole.mutate({ userId: user.id, role: e.target.value });
  }

  return (
    <div className="flex items-center gap-2">
      <Badge
        variant="outline"
        className={
          roleBadgeClass[user.role] ??
          'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
        }
      >
        {user.role}
      </Badge>
      <select
        className="rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
        value={user.role}
        onChange={handleChange}
        disabled={updateRole.isPending}
        aria-label={`Role for ${user.email}`}
      >
        {VALID_ROLES.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
    </div>
  );
}

export function UserRolesCard() {
  const { data, isLoading } = useTenantUsers();
  const users = data?.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4" />
          User Roles
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading users...</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-muted-foreground">No users found in this workspace.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="pb-2 text-left font-medium">Email</th>
                  <th className="pb-2 text-left font-medium">Name</th>
                  <th className="pb-2 text-left font-medium">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="py-2 pr-4 font-mono text-xs">{user.email}</td>
                    <td className="py-2 pr-4 text-muted-foreground">{user.name ?? '—'}</td>
                    <td className="py-2">
                      <RoleDropdown user={user} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
